import { RosettaSDKUtils, SpecialProductTemplate } from '@axon/rosetta-sdk';
import { AssetTypes } from 'libs.localization.common';
import { ParticipantStatusesPreview, ProtocolSchedulePreview, ResourcesPreview, SignInSupportInformationPreview, StudyNameWithPIIPreview, StudyNameWithNoPIIPreview, ActivityNamesPreview, SiteVisitSchedulePreview, ResourceTitlesPreview, PushNotificationPreview, EmailPreview, SmsPreview, } from './components';
import { StudyNameWithMixPIIPreview as StudyNameWithMixPIIPreviewR520, StudyNameWithPIIPreview as StudyNameWithPIIPreviewR520, } from './components_520';
import { ParticipantStatusesPreview as ParticipantStatusesPreviewR400, ProtocolSchedulePreview as ProtocolSchedulePreviewR400, ResourcesPreview as ResourcesPreviewR400, SignInSupportInformationPreview as SignInSupportInformationPreviewR400, StudyNameWithPIIPreview as StudyNameWithPIIPreviewR400, StudyNameWithNoPIIPreview as StudyNameWithNoPIIPreviewR400, ActivityNamesPreview as ActivityNamesPreviewR400, SiteVisitSchedulePreview as SiteVisitSchedulePreviewR400, ResourceTitlesPreview as ResourceTitlesPreviewR400, PushNotificationPreview as PushNotificationPreviewR400, EmailPreview as EmailPreviewR400, SmsPreview as SmsPreviewR400, } from './components_r400';
// Version-based component mappings for future extensibility
const VERSION_COMPONENT_MAPPINGS = {
    // Default/legacy components (for versions < 400)
    0: {
        [AssetTypes.PARTICIPANT_STATUSES]: ParticipantStatusesPreview,
        [AssetTypes.PROTOCOL_SCHEDULE]: ProtocolSchedulePreview,
        [AssetTypes.SIGN_IN_STUDY_NAME_PII]: StudyNameWithPIIPreview,
        [AssetTypes.SIGN_IN_STUDY_NAME]: StudyNameWithNoPIIPreview,
        [AssetTypes.SITE_VISIT_SCHEDULE]: SiteVisitSchedulePreview,
        [AssetTypes.SIGN_IN_SUPPORT_INFORMATION]: SignInSupportInformationPreview,
        [AssetTypes.RESOURCES]: ResourcesPreview,
        [AssetTypes.ACTIVITY_NAMES]: ActivityNamesPreview,
        [AssetTypes.RESOURCE_TITLES]: ResourceTitlesPreview,
        [AssetTypes.NOTIFICATION_PUSH_NOTIFICATION]: PushNotificationPreview,
        [AssetTypes.NOTIFICATION_EMAIL]: EmailPreview,
        [AssetTypes.NOTIFICATION_SMS]: SmsPreview,
    },
    // R400 components (for versions >= 400)
    400: {
        [AssetTypes.PARTICIPANT_STATUSES]: ParticipantStatusesPreviewR400,
        [AssetTypes.PROTOCOL_SCHEDULE]: ProtocolSchedulePreviewR400,
        [AssetTypes.SIGN_IN_STUDY_NAME_PII]: StudyNameWithPIIPreviewR400,
        [AssetTypes.SIGN_IN_STUDY_NAME]: StudyNameWithNoPIIPreviewR400,
        [AssetTypes.SITE_VISIT_SCHEDULE]: SiteVisitSchedulePreviewR400,
        [AssetTypes.SIGN_IN_SUPPORT_INFORMATION]: SignInSupportInformationPreviewR400,
        [AssetTypes.RESOURCES]: ResourcesPreviewR400,
        [AssetTypes.ACTIVITY_NAMES]: ActivityNamesPreviewR400,
        [AssetTypes.RESOURCE_TITLES]: ResourceTitlesPreviewR400,
        [AssetTypes.NOTIFICATION_PUSH_NOTIFICATION]: PushNotificationPreviewR400,
        [AssetTypes.NOTIFICATION_EMAIL]: EmailPreviewR400,
        [AssetTypes.NOTIFICATION_SMS]: SmsPreviewR400,
    },
    520: {
        [AssetTypes.SIGN_IN_STUDY_NAME_MIX_PII]: StudyNameWithMixPIIPreviewR520,
        [AssetTypes.SIGN_IN_STUDY_NAME_PII]: StudyNameWithPIIPreviewR520,
    },
};
// Resolves the highest component bucket whose threshold is <= the requested product
// version and that satisfies `isApplicable`. Comparison is delegated to the Rosetta SDK
// (semver-aware, dev-latest aware) instead of ad-hoc parsing of the `r###` string.
function resolveApplicableBucket(productVersion, isApplicable) {
    const normalized = productVersion.toLowerCase();
    const isDevLatest = normalized === SpecialProductTemplate.DEV_LATEST;
    // Preserve existing behavior: empty / malformed versions fall back to the legacy bucket.
    if (!normalized || (!isDevLatest && !/^r\d+(\.\d+\.\d+)?$/.test(normalized))) {
        return 0;
    }
    // Sort versions in descending order to find the highest applicable version.
    const sortedVersions = Object.keys(VERSION_COMPONENT_MAPPINGS)
        .map(Number)
        .sort((a, b) => b - a);
    try {
        for (const version of sortedVersions) {
            // 0 is the default fallback returned below; nothing to compare against.
            if (version === 0) {
                continue;
            }
            // `!isTemplateOlderThan(x, threshold)` means "x >= threshold". dev-latest is never
            // older than anything, so it resolves to the highest applicable bucket.
            if (!RosettaSDKUtils.isTemplateOlderThan(normalized, `r${version}`) && isApplicable(version)) {
                return version;
            }
        }
    }
    catch (_a) {
        // Defensive: any unexpected parse failure falls back to the legacy bucket.
    }
    return 0;
}
export function getApplicableVersion(productVersion = '') {
    return resolveApplicableBucket(productVersion, () => true);
}
export function getPreviewComponents(productVersion = '') {
    const applicableVersion = getApplicableVersion(productVersion);
    return VERSION_COMPONENT_MAPPINGS[applicableVersion];
}
export function getApplicableVersionByAssetType(productVersion = '', assetType) {
    return resolveApplicableBucket(productVersion, (version) => { var _a; return Boolean((_a = VERSION_COMPONENT_MAPPINGS[version]) === null || _a === void 0 ? void 0 : _a[assetType]); });
}
export function getPreviewComponentByAssetType(productVersion = '', assetType) {
    const applicableVersion = getApplicableVersionByAssetType(productVersion, assetType);
    const versionMapping = VERSION_COMPONENT_MAPPINGS[applicableVersion];
    return versionMapping === null || versionMapping === void 0 ? void 0 : versionMapping[assetType];
}
//# sourceMappingURL=preview_component_mapping.js.map